import { useCallback, useMemo, useState, type FC } from "react";
import {
    CommonTable,
    CommonTableHeaderCell,
    CommonTableEditHeaderCell,
    CommonTableRow,
    CommonTableCell,
    CommonTableEditCell,
} from "./table";
import {
    ColumnFilterPopup,
    useColumnFilter,
    type SortDirection,
} from "./molecules/ColumnFilterPopup";

export interface DayOffTypeRow {
    id: number;
    code: string;
    name: string;
    short_name: string;
    description: string;
    background_color: string;
    text_color: string;
    is_work_time: number; // 1:する 2:しない
    day_count_type: number; // 1:1日 2:0.5日 3:カウントしない
    is_system: number; // 1:システム定義
}

interface DayOffTypeTableProps {
    rows: DayOffTypeRow[];
    isLoading: boolean;
    onEditRow: (row: DayOffTypeRow) => void;
}

const IS_WORK_TIME_LABELS: Record<number, string> = { 1: "する", 2: "しない" };
const DAY_COUNT_LABELS: Record<number, string> = { 1: "勤務日数", 2: "公休日数", 3: "カウントしない" };

const DAY_OFF_TYPE_COLUMN_COUNT = 8;
const EMPTY_VALUE_KEY = "__empty__";
const EMPTY_VALUE_LABEL = "(未設定)";

type SortColumn = "code" | "name" | "short" | "desc" | "work" | "count";

const toKey = (val: string | null | undefined): string =>
    val === null || val === undefined || val === "" ? EMPTY_VALUE_KEY : val;

const labelOrEmpty = (key: string): string =>
    key === EMPTY_VALUE_KEY ? EMPTY_VALUE_LABEL : key;

const DayOffTypeTable: FC<DayOffTypeTableProps> = ({ rows, isLoading, onEditRow }) => {
    // 列ごとの並び替え/絞り込み state
    const codeFilter = useColumnFilter(rows, (r) => toKey(r.code), labelOrEmpty);
    const nameFilter = useColumnFilter(rows, (r) => toKey(r.name), labelOrEmpty);
    const shortFilter = useColumnFilter(rows, (r) => toKey(r.short_name), labelOrEmpty);
    const descFilter = useColumnFilter(rows, (r) => toKey(r.description), labelOrEmpty);
    const workFilter = useColumnFilter(
        rows,
        (r) => String(r.is_work_time),
        (k) => IS_WORK_TIME_LABELS[Number(k)] ?? EMPTY_VALUE_LABEL,
    );
    const countFilter = useColumnFilter(
        rows,
        (r) => String(r.day_count_type),
        (k) => DAY_COUNT_LABELS[Number(k)] ?? EMPTY_VALUE_LABEL,
    );

    // 並び替えは「同時に1列のみ」アクティブ
    const [activeSort, setActiveSort] = useState<{
        column: SortColumn;
        direction: "asc" | "desc";
    } | null>(null);

    const sortFor = (column: SortColumn): SortDirection =>
        activeSort?.column === column ? activeSort.direction : null;

    const handleSortChange = useCallback(
        (column: SortColumn) => (dir: SortDirection) => {
            setActiveSort(dir === null ? null : { column, direction: dir });
        },
        [],
    );

    // 表示用にソート/絞り込みを適用
    const displayedRows = useMemo(() => {
        let list = rows;

        if (codeFilter.isFiltering) {
            const set = new Set(codeFilter.selected);
            list = list.filter((r) => set.has(toKey(r.code)));
        }
        if (nameFilter.isFiltering) {
            const set = new Set(nameFilter.selected);
            list = list.filter((r) => set.has(toKey(r.name)));
        }
        if (shortFilter.isFiltering) {
            const set = new Set(shortFilter.selected);
            list = list.filter((r) => set.has(toKey(r.short_name)));
        }
        if (descFilter.isFiltering) {
            const set = new Set(descFilter.selected);
            list = list.filter((r) => set.has(toKey(r.description)));
        }
        if (workFilter.isFiltering) {
            const set = new Set(workFilter.selected);
            list = list.filter((r) => set.has(String(r.is_work_time)));
        }
        if (countFilter.isFiltering) {
            const set = new Set(countFilter.selected);
            list = list.filter((r) => set.has(String(r.day_count_type)));
        }

        if (activeSort) {
            const cmp = (a: DayOffTypeRow, b: DayOffTypeRow): number => {
                switch (activeSort.column) {
                    case "code":
                        return (a.code ?? "").localeCompare(b.code ?? "", "ja");
                    case "name":
                        return (a.name ?? "").localeCompare(b.name ?? "", "ja");
                    case "short":
                        return (a.short_name ?? "").localeCompare(b.short_name ?? "", "ja");
                    case "desc":
                        return (a.description ?? "").localeCompare(b.description ?? "", "ja");
                    case "work":
                        return (IS_WORK_TIME_LABELS[a.is_work_time] ?? "").localeCompare(
                            IS_WORK_TIME_LABELS[b.is_work_time] ?? "",
                            "ja",
                        );
                    case "count":
                        return (DAY_COUNT_LABELS[a.day_count_type] ?? "").localeCompare(
                            DAY_COUNT_LABELS[b.day_count_type] ?? "",
                            "ja",
                        );
                }
            };
            list = [...list].sort((a, b) =>
                activeSort.direction === "asc" ? cmp(a, b) : -cmp(a, b),
            );
        }

        return list;
    }, [
        rows,
        codeFilter.isFiltering, codeFilter.selected,
        nameFilter.isFiltering, nameFilter.selected,
        shortFilter.isFiltering, shortFilter.selected,
        descFilter.isFiltering, descFilter.selected,
        workFilter.isFiltering, workFilter.selected,
        countFilter.isFiltering, countFilter.selected,
        activeSort,
    ]);

    // ヘッダークリック時、ポップアップは「同時に1つだけ」開く
    const openOnly = useCallback(
        (which: SortColumn, anchor: HTMLButtonElement) => {
            const map = {
                code: codeFilter,
                name: nameFilter,
                short: shortFilter,
                desc: descFilter,
                work: workFilter,
                count: countFilter,
            } as const;
            const target = map[which];
            target.anchorRef.current = anchor;
            const willOpen = !target.open;
            target.setOpen(willOpen);
            if (willOpen) {
                Object.entries(map).forEach(([col, f]) => {
                    if (col !== which) f.setOpen(false);
                });
            }
        },
        [codeFilter, nameFilter, shortFilter, descFilter, workFilter, countFilter],
    );

    return (
        <>
            <CommonTable>
                <thead className="sticky top-0 z-20">
                    <tr>
                        <CommonTableEditHeaderCell />
                        <CommonTableHeaderCell
                            sortable
                            sortActive={codeFilter.open}
                            sortDirection={sortFor("code")}
                            filterCount={codeFilter.filterCount}
                            onSort={(anchor) => openOnly("code", anchor)}
                            className="min-w-[160px]"
                        >
                            休日コード
                        </CommonTableHeaderCell>
                        <CommonTableHeaderCell
                            sortable
                            sortActive={nameFilter.open}
                            sortDirection={sortFor("name")}
                            filterCount={nameFilter.filterCount}
                            onSort={(anchor) => openOnly("name", anchor)}
                            className="min-w-[120px]"
                        >
                            休日名
                        </CommonTableHeaderCell>
                        <CommonTableHeaderCell
                            sortable
                            sortActive={shortFilter.open}
                            sortDirection={sortFor("short")}
                            filterCount={shortFilter.filterCount}
                            onSort={(anchor) => openOnly("short", anchor)}
                            className="min-w-[96px]"
                        >
                            略称
                        </CommonTableHeaderCell>
                        <CommonTableHeaderCell
                            sortable
                            sortActive={descFilter.open}
                            sortDirection={sortFor("desc")}
                            filterCount={descFilter.filterCount}
                            onSort={(anchor) => openOnly("desc", anchor)}
                            className="min-w-[200px]"
                        >
                            備考
                        </CommonTableHeaderCell>
                        <CommonTableHeaderCell
                            sortable
                            sortActive={workFilter.open}
                            sortDirection={sortFor("work")}
                            filterCount={workFilter.filterCount}
                            onSort={(anchor) => openOnly("work", anchor)}
                            className="min-w-[144px]"
                        >
                            勤務時間に含める
                        </CommonTableHeaderCell>
                        <CommonTableHeaderCell
                            sortable
                            sortActive={countFilter.open}
                            sortDirection={sortFor("count")}
                            filterCount={countFilter.filterCount}
                            onSort={(anchor) => openOnly("count", anchor)}
                            className="min-w-[128px]"
                        >
                            日数カウント
                        </CommonTableHeaderCell>
                        <CommonTableHeaderCell className="min-w-[120px]">プレビュー</CommonTableHeaderCell>
                    </tr>
                </thead>
                <tbody>
                    {isLoading ? (
                        <CommonTableRow>
                            <CommonTableCell colSpan={DAY_OFF_TYPE_COLUMN_COUNT} className="py-8 text-gray-500">
                                読み込み中...
                            </CommonTableCell>
                        </CommonTableRow>
                    ) : displayedRows.length === 0 ? (
                        <CommonTableRow>
                            <CommonTableCell colSpan={DAY_OFF_TYPE_COLUMN_COUNT} className="py-8 text-gray-400">
                                {rows.length === 0
                                    ? "休日種類が登録されていません。「新規登録」ボタンで追加してください。"
                                    : "該当する休日種類がありません"}
                            </CommonTableCell>
                        </CommonTableRow>
                    ) : (
                        displayedRows.map((row) => (
                            <CommonTableRow key={row.id}>
                                <CommonTableEditCell
                                    onClick={() => onEditRow(row)}
                                    disabled={row.is_system === 1}
                                />
                                <CommonTableCell>{row.code}</CommonTableCell>
                                <CommonTableCell>{row.name}</CommonTableCell>
                                <CommonTableCell>{row.short_name || "-"}</CommonTableCell>
                                <CommonTableCell>{row.description || "-"}</CommonTableCell>
                                <CommonTableCell>{IS_WORK_TIME_LABELS[row.is_work_time] ?? "-"}</CommonTableCell>
                                <CommonTableCell>{DAY_COUNT_LABELS[row.day_count_type] ?? "-"}</CommonTableCell>
                                <CommonTableCell>
                                    <span
                                        className="inline-block rounded px-2 py-0.5 text-sm font-medium"
                                        style={{ backgroundColor: row.background_color, color: row.text_color }}
                                    >
                                        {row.short_name || row.name || "---"}
                                    </span>
                                </CommonTableCell>
                            </CommonTableRow>
                        ))
                    )}
                </tbody>
            </CommonTable>

            {/* 列ごとの並び替え＋絞り込みポップアップ */}
            <ColumnFilterPopup
                isOpen={codeFilter.open}
                onClose={() => codeFilter.setOpen(false)}
                anchorRef={codeFilter.anchorRef}
                sortDirection={sortFor("code")}
                onSortChange={handleSortChange("code")}
                options={codeFilter.options}
                selectedValues={codeFilter.selected}
                onSelectedValuesChange={codeFilter.setSelected}
            />
            <ColumnFilterPopup
                isOpen={nameFilter.open}
                onClose={() => nameFilter.setOpen(false)}
                anchorRef={nameFilter.anchorRef}
                sortDirection={sortFor("name")}
                onSortChange={handleSortChange("name")}
                options={nameFilter.options}
                selectedValues={nameFilter.selected}
                onSelectedValuesChange={nameFilter.setSelected}
            />
            <ColumnFilterPopup
                isOpen={shortFilter.open}
                onClose={() => shortFilter.setOpen(false)}
                anchorRef={shortFilter.anchorRef}
                sortDirection={sortFor("short")}
                onSortChange={handleSortChange("short")}
                options={shortFilter.options}
                selectedValues={shortFilter.selected}
                onSelectedValuesChange={shortFilter.setSelected}
            />
            <ColumnFilterPopup
                isOpen={descFilter.open}
                onClose={() => descFilter.setOpen(false)}
                anchorRef={descFilter.anchorRef}
                sortDirection={sortFor("desc")}
                onSortChange={handleSortChange("desc")}
                options={descFilter.options}
                selectedValues={descFilter.selected}
                onSelectedValuesChange={descFilter.setSelected}
            />
            <ColumnFilterPopup
                isOpen={workFilter.open}
                onClose={() => workFilter.setOpen(false)}
                anchorRef={workFilter.anchorRef}
                sortDirection={sortFor("work")}
                onSortChange={handleSortChange("work")}
                options={workFilter.options}
                selectedValues={workFilter.selected}
                onSelectedValuesChange={workFilter.setSelected}
            />
            <ColumnFilterPopup
                isOpen={countFilter.open}
                onClose={() => countFilter.setOpen(false)}
                anchorRef={countFilter.anchorRef}
                sortDirection={sortFor("count")}
                onSortChange={handleSortChange("count")}
                options={countFilter.options}
                selectedValues={countFilter.selected}
                onSelectedValuesChange={countFilter.setSelected}
            />
        </>
    );
};

export default DayOffTypeTable;
